// Studio: Eigenschaften des Bausteins „Gebetszeiten" (Quelle, Berechnung, Iqama, Anzeige).
import {
  CLOCK_TIME_ZONES, HIGH_LAT_RULES, IQAMA_KEYS, IQAMA_RE, JUMUAH_RE, PRAYER_KEYS, PRAYER_LANGS, PRAYER_METHODS, PRAYER_SOURCES, PRAYER_VIEWS,
} from '@disploraq/layouts';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { usePrayerProviders, usePrayerTables } from '../../api/prayer';
import { PlaceSearch } from '../../components/PlaceSearch';
import '../../styles/prayer.css';

type Rec = Record<string, unknown>;

/** Eingabe, die erst bei gültigem Wert übernommen wird (sonst Hinweis, Layout bleibt speicherbar). */
function Checked({ label, value, re, disabled, placeholder, onValid }: {
  label: string; value: string; re: RegExp; disabled: boolean; placeholder?: string; onValid: (v: string) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const ok = re.test(draft.trim());
  return (
    <label className="field">{label}
      <input disabled={disabled} value={draft} placeholder={placeholder} aria-invalid={!ok} spellCheck={false}
        onChange={(e) => { setDraft(e.target.value); if (re.test(e.target.value.trim())) onValid(e.target.value.trim()); }} />
      {!ok && <span className="small" style={{ color: 'var(--err)' }}>{t('prayer.f.invalid')}</span>}
    </label>
  );
}

export function PrayerFields({ rec, disabled, set, patch }: {
  rec: Rec; disabled: boolean; set: (k: string, v: unknown) => void; patch: (p: Rec) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'de';
  const source = (rec.source as string | undefined) ?? 'calc';
  const method = (rec.method as string | undefined) ?? 'mwl';
  const tables = usePrayerTables();
  const providers = usePrayerProviders();
  const provider = providers.data?.find((p) => p.key === rec.provider);
  const adjust = (rec.adjust as Record<string, number> | undefined) ?? {};
  const iqama = (rec.iqama as Record<string, string> | undefined) ?? {};

  const sel = (key: string, opts: readonly string[], def: string, ns = key) => (
    <label key={key} className="field">{t(`prayer.f.${key}`)}
      <select disabled={disabled} value={String(rec[key] ?? def)} onChange={(e) => set(key, e.target.value)}>
        {opts.map((o) => <option key={o} value={o}>{t(`prayer.o.${ns}.${o}`)}</option>)}
      </select>
    </label>
  );
  const num = (key: string, step: number, min: number, max: number) => (
    <label key={key} className="field">{t(`prayer.f.${key}`)}
      <input type="number" step={step} min={min} max={max} disabled={disabled} value={(rec[key] as number | undefined) ?? ''}
        onChange={(e) => set(key, e.target.value === '' ? undefined : Math.min(max, Math.max(min, Number(e.target.value))))} />
    </label>
  );
  const check = (key: string, def = true) => (
    <label key={key} className="check"><input type="checkbox" disabled={disabled} checked={(rec[key] as boolean | undefined) ?? def} onChange={(e) => set(key, e.target.checked)} />{t(`prayer.f.${key}`)}</label>
  );
  const coords = (
    <div className="two">
      <label className="field">{t('studio.f.lat')}<input type="number" step="0.0001" disabled={disabled} value={(rec.lat as number | null) ?? ''} onChange={(e) => set('lat', e.target.value === '' ? null : Number(e.target.value))} /></label>
      <label className="field">{t('studio.f.lon')}<input type="number" step="0.0001" disabled={disabled} value={(rec.lon as number | null) ?? ''} onChange={(e) => set('lon', e.target.value === '' ? null : Number(e.target.value))} /></label>
    </div>
  );
  const tz = (
    <label className="field">{t('prayer.f.timeZone')}
      <select disabled={disabled} value={(rec.timeZone as string) ?? ''} onChange={(e) => set('timeZone', e.target.value || undefined)}>
        <option value="">{t('studio.zoneLocal')}</option>
        {[...new Set([...(rec.timeZone ? [rec.timeZone as string] : []), ...CLOCK_TIME_ZONES])].map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
      </select>
    </label>
  );

  return (
    <div className="prayer-fields">
      {sel('source', PRAYER_SOURCES, 'calc')}

      {source === 'table' && <>
        <label className="field">{t('prayer.f.table')}
          <select disabled={disabled} value={(rec.table as string) ?? ''} onChange={(e) => set('table', e.target.value || undefined)}>
            <option value="">{t('design.choose')}</option>
            {(tables.data ?? []).map((x) => <option key={x.id} value={x.id}>{x.name} ({x.firstDate} – {x.lastDate})</option>)}
          </select>
        </label>
        <div className="hint small">{t('prayer.f.tableHint')} <Link to="/prayer-times">{t('prayer.f.manageTables')}</Link></div>
        {tz}
      </>}

      {source === 'provider' && <>
        <label className="field">{t('prayer.f.provider')}
          <select disabled={disabled} value={(rec.provider as string) ?? ''} onChange={(e) => patch({ provider: e.target.value || undefined, location: undefined })}>
            <option value="">{t('design.choose')}</option>
            {(providers.data ?? []).map((p) => <option key={p.key} value={p.key} disabled={!p.enabled}>{p.name[lang]}{p.enabled ? '' : ` (${t('prayer.providerOff')})`}</option>)}
          </select>
        </label>
        {provider && <>
          <label className="field">{t('prayer.f.location')}
            <select disabled={disabled} value={(rec.location as string) ?? ''} onChange={(e) => set('location', e.target.value || undefined)}>
              <option value="">{t('design.choose')}</option>
              {provider.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </label>
          <div className="hint small">{provider.description[lang]} {t('prayer.f.attribution', { text: provider.attribution })}</div>
        </>}
      </>}

      {(source === 'calc' || source === 'table') && <>
        <PlaceSearch label={t('prayer.f.place')} value={(rec.place as string) ?? ''} disabled={disabled} onText={(x) => set('place', x)}
          onPick={(p) => patch({ place: p.name, lat: Math.round(p.lat * 10_000) / 10_000, lon: Math.round(p.lon * 10_000) / 10_000, ...(p.timezone ? { timeZone: p.timezone } : {}) })} />
        {coords}
        {source === 'table' && <div className="hint small">{t('prayer.f.fallbackHint')}</div>}
        {source === 'calc' && tz}
      </>}

      {(source === 'calc' || source === 'table') && <details className="prayer-calc" open={source === 'calc'}>
        <summary>{t('prayer.f.calculation')}</summary>
        {sel('method', PRAYER_METHODS, 'mwl')}
        {method === 'custom' && <div className="two">{num('fajrAngle', 0.5, 1, 30)}{num('ishaAngle', 0.5, 1, 30)}</div>}
        {method === 'custom' && num('ishaInterval', 1, 0, 180)}
        {sel('asr', ['shafi', 'hanafi'], 'shafi')}
        {sel('highLat', HIGH_LAT_RULES, 'auto')}
        <div className="field">{t('prayer.f.adjust')}
          <div className="prayer-grid">
            {PRAYER_KEYS.map((k) => (
              <label key={k} className="small">{t(`prayer.p.${k}`)}
                <input type="number" step={1} min={-60} max={60} disabled={disabled} value={adjust[k] ?? 0}
                  onChange={(e) => set('adjust', { ...adjust, [k]: Math.min(60, Math.max(-60, Math.round(Number(e.target.value) || 0))) })} />
              </label>
            ))}
          </div>
        </div>
      </details>}

      <details className="prayer-iqama">
        <summary>{t('prayer.f.iqamaTitle')}</summary>
        <div className="hint small">{t('prayer.f.iqamaHint')}</div>
        <div className="prayer-grid">
          {IQAMA_KEYS.map((k) => (
            <Checked key={k} label={t(`prayer.p.${k}`)} value={iqama[k] ?? ''} re={IQAMA_RE} disabled={disabled} placeholder="+10"
              onValid={(v) => set('iqama', { ...iqama, [k]: v })} />
          ))}
        </div>
        <Checked label={t('prayer.f.jumuah')} value={(rec.jumuah as string) ?? ''} re={JUMUAH_RE} disabled={disabled} placeholder="13:15, 14:30"
          onValid={(v) => set('jumuah', v)} />
      </details>

      <details className="prayer-view" open>
        <summary>{t('prayer.f.display')}</summary>
        {sel('view', PRAYER_VIEWS, 'table')}
        {sel('lang', PRAYER_LANGS, 'de')}
        <label className="field">{t('prayer.f.title')}<input disabled={disabled} value={(rec.title as string) ?? ''} onChange={(e) => set('title', e.target.value)} /></label>
        {check('showDate')}{check('showHijri')}
        {(rec.showHijri ?? true) !== false && (
          <label className="field">{t('prayer.f.hijriAdjust')}
            <select disabled={disabled} value={String(rec.hijriAdjust ?? 0)} onChange={(e) => set('hijriAdjust', Number(e.target.value))}>
              {[-2, -1, 0, 1, 2].map((n) => <option key={n} value={n}>{n > 0 ? `+${n}` : n}</option>)}
            </select>
          </label>
        )}
        {check('showSunrise')}{check('showIqama')}{check('showCountdown')}
        {check('notice', false)}
        {!!rec.notice && <>
          <label className="field">{t('prayer.f.noticeText')}<input disabled={disabled} value={(rec.noticeText as string) ?? ''} placeholder={t('prayer.f.noticePlaceholder')} onChange={(e) => set('noticeText', e.target.value)} /></label>
          {num('noticeMinutes', 1, 1, 60)}
        </>}
        <label className="field">{t('studio.f.size')} <span className="muted small">{String(rec.size ?? '')}</span>
          <input type="range" min={1.5} max={8} step={0.1} disabled={disabled} value={(rec.size as number) ?? 3.2} onChange={(e) => set('size', Number(e.target.value))} />
        </label>
        <div className="two">
          <label className="field">{t('studio.f.color')}<input type="color" disabled={disabled} value={(rec.color as string) || '#ffffff'} onChange={(e) => set('color', e.target.value)} /></label>
          <label className="field">{t('prayer.f.accent')}<input type="color" disabled={disabled} value={(rec.accent as string) || '#E0B04B'} onChange={(e) => set('accent', e.target.value)} /></label>
        </div>
      </details>
    </div>
  );
}
